import { withRentalProductOffer } from '../../utilities/productStructuredData'
import React from 'react'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { BreadcrumbJsonLd, LocalBusinessJsonLd, NextSeo } from 'next-seo'
import { useDispatch, useSelector } from 'react-redux'
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle'
import { FiMic } from '@react-icons/all-files/fi/FiMic'
import { FiMonitor } from '@react-icons/all-files/fi/FiMonitor'
import { FiShoppingBag } from '@react-icons/all-files/fi/FiShoppingBag'
import { FiUsers } from '@react-icons/all-files/fi/FiUsers'
import { wrapper } from '../../store/configStore'
import { SERVER_IP } from '../../store/middleware/api'
import { getFilteredProducts, productsReceived, productsSetFilters } from '../../store/slices/entities/products'
import { getActiveUser } from '../../store/slices/user'
import { SHOW_POPUP } from '../../store/slices/ui'
import { BUSINESS_HOURS } from '../../content/businessHours'
import AudioClosingActionBand from '../../components/design-system/marketing/AudioClosingActionBand'
import CatalogHelp from '../../components/design-system/commerce/CatalogHelp'
import Container from '../../components/design-system/foundations/Container'
import EquipmentGuide from '../../components/design-system/marketing/EquipmentGuide'
import FAQGroup from '../../components/design-system/marketing/FAQGroup'
import MediaFeature from '../../components/design-system/marketing/MediaFeature'
import PageShell from '../../components/design-system/foundations/PageShell'
import ProductCatalog from '../../components/design-system/commerce/ProductCatalog'
import RelatedServices from '../../components/design-system/marketing/RelatedServices'
import ReviewProof from '../../components/design-system/marketing/ReviewProof'
import ServiceCategoryGrid from '../../components/design-system/marketing/ServiceCategoryGrid'
import ServiceHero from '../../components/design-system/marketing/ServiceHero'
import ServiceIntroduction from '../../components/design-system/marketing/ServiceIntroduction'
import VisualDecisionGuide from '../../components/design-system/marketing/VisualDecisionGuide'
import QuotePop from '../../components/DeferredQuotePopup'
import * as gtag from '../../lib/gtag'
import { compactCatalogProducts } from '../../utilities/catalogPublicPayload'
import { getAudioFamilyBreadcrumbs, toBreadcrumbJsonLdItems, toHeroBreadcrumbs } from '../../utilities/audioFamilyBreadcrumbs'
import { isProductAvailableInMarket } from '../../utilities/fulfillmentMarket'
import { requestPageData } from '../../utilities/boundedPageData'
import orlandoKaraokeHero from '../../public/Pictures/categories-v2/orlando-karaoke-rental-hero-v2.jpg'
import karaokeHomeSetup from '../../public/Pictures/categories-v2/sound-karaoke-home-setup-v2.jpg'
import karaokeMachine from '../../public/Pictures/karaokemachine.jpg'
import karaokeSinger from '../../public/Pictures/category-karaoke.jpg'

const SeoPageDrawer = dynamic(() => import('../../components/SeoPageDrawer'), { ssr: false })

const PAGE_PATH = '/orlando/karaoke-rental'
const CANONICAL = `https://miamisoundrental.com${PAGE_PATH}`
const TITLE = 'Karaoke Machine Rental Orlando | Complete Packages'
const DESCRIPTION = 'Rent a complete karaoke system in Orlando or Tampa with microphones, speakers and a lyrics display. Compare local packages, dates, pickup, delivery and setup.'
const ORLANDO_REVIEWS_URL = 'https://www.google.com/maps/search/?api=1&query=Miami+Sound+Rental+Orlando+FL'

const questions = [
  {
    question: 'What is included in an Orlando karaoke rental package?',
    answer: 'Package contents vary, but complete karaoke systems normally combine microphones, speakers, a mixer or control system, and a TV or other lyrics display. Review the included-items list on the package before reserving because the number of microphones, display size, stands, cables, and playback equipment are not identical in every option.'
  },
  {
    question: 'Can I choose two or four wireless microphones?',
    answer: 'Yes. Compare the Microphones specification in the catalog to find packages intended for one or two singers, larger groups, or faster handoffs between guests. The package details show the exact microphone count and whether the microphones are wired or wireless.'
  },
  {
    question: 'Does the karaoke package include a TV or projector?',
    answer: 'Many complete packages include a lyrics display, but the display type and size vary. Some use a TV, while larger-room packages may use a projector and screen. Confirm the Screen Size and package contents before selecting dates.'
  },
  {
    question: 'How do guests access the karaoke songs?',
    answer: 'Song access depends on the selected package and its playback system. The current product details identify the included playback equipment and software. Ask our team before booking if your venue has limited internet access or you need a particular song library or connection method.'
  },
  {
    question: 'Can I pick up a karaoke machine from the Orlando warehouse?',
    answer: 'Eligible packages can be picked up from our Orlando branch. Choose the dates first to see live availability and the fulfillment options offered for that package. Larger or more complex systems may require delivery and setup.'
  },
  {
    question: 'Do you deliver karaoke rentals to Tampa?',
    answer: 'Yes. Tampa and Central Florida requests are handled by our Orlando team when the equipment and travel window are available. Enter the Tampa venue after choosing the package and dates so the correct delivery, setup, and collection price can be confirmed.'
  },
  {
    question: 'Does the rental include a karaoke host or operator?',
    answer: 'Not by default. This page is for equipment rental packages. If you need an operator, an emcee, or managed event support, request help before booking so the required labor and event schedule can be reviewed separately.'
  },
  {
    question: 'What size karaoke package should I choose?',
    answer: 'Start with the audience and room, then compare speaker coverage, microphone count, and lyrics-display size. A compact package can fit a home, small party, or meeting room. Larger celebrations and corporate programs usually need wider sound coverage and a display that remains readable from farther away.'
  }
]

const textFromHtml = value => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
const absoluteProductUrl = value => {
  if (!value) return CANONICAL
  try {
    return new URL(value, 'https://miamisoundrental.com').toString()
  } catch (error) {
    return CANONICAL
  }
}

export const getStaticProps = wrapper.getStaticProps(async ({ store }) => {
  const [productResponse, reviewResponse] = await Promise.all([
    requestPageData({
      required: true,
      validateData: Array.isArray,
      url: `${SERVER_IP}/inventory/product`,
      params: {
        msr: true,
        cat: 'Karaoke'
      },
      label: `products for ${PAGE_PATH}`
    }),
    requestPageData({
      url: `${SERVER_IP}/google-review/public`,
      params: {
        page: PAGE_PATH,
        limit: 3
      },
      fallback: null,
      label: `reviews for ${PAGE_PATH}`
    })
  ])

  const products = Array.isArray(productResponse.data) ? productResponse.data : []
  store.dispatch({ type: productsReceived.type, payload: compactCatalogProducts(products) })
  store.dispatch({ type: productsSetFilters.type, payload: { static: true } })

  const usedFallback = [productResponse, reviewResponse].some(response => response.usedFallback)

  return {
    props: { reviewData: reviewResponse.data },
    revalidate: usedFallback ? 60 : 3600
  }
})

const OrlandoKaraokeRental = ({ reviewData }) => {
  const products = useSelector(getFilteredProducts)
  const user = useSelector(getActiveUser)
  const dispatch = useDispatch()
  const breadcrumbs = getAudioFamilyBreadcrumbs(PAGE_PATH)
  const karaokeProducts = products.filter(product => product.categories?.includes('Sound/Karaoke'))
  const localSchemaProducts = karaokeProducts.filter(product => isProductAvailableInMarket(product, 'Orlando'))

  const openQuote = () => {
    gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders' })
    dispatch(SHOW_POPUP({ key: 'quote', size: 'medium', body: <QuotePop /> }))
  }

  const trackCall = () => gtag.event({
    action: 'booking_assistance_call_CTA',
    label: 'leads',
    value: 2,
    category: 'orders'
  })

  const primaryAction = {
    label: 'Browse Orlando Karaoke Packages',
    href: '#orlando-karaoke-catalog',
    icon: FiShoppingBag,
    conversionKey: 'orlando-karaoke-rental.hero.browse-packages',
    channel: 'rental',
    salesPath: 'self_service',
    onClick: () => gtag.event({ action: 'catalog_click', label: 'leads', value: 2, category: 'orders' })
  }

  const secondaryAction = {
    label: 'Get Karaoke Package Help',
    icon: FiMessageCircle,
    conversionKey: 'orlando-karaoke-rental.hero.ask-specialist',
    channel: 'quote',
    salesPath: 'managed',
    onClick: openQuote
  }

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${CANONICAL}#service`,
    name: 'Karaoke Machine Rental in Orlando and Central Florida',
    serviceType: 'Complete karaoke machine and equipment package rental',
    url: CANONICAL,
    description: DESCRIPTION,
    provider: { '@id': 'https://miamisoundrental.com/orlando#localbusiness' },
    areaServed: [
      { '@type': 'City', name: 'Orlando' },
      { '@type': 'City', name: 'Tampa' },
      { '@type': 'AdministrativeArea', name: 'Central Florida' }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Orlando karaoke machine rental packages',
      itemListElement: (localSchemaProducts.length ? localSchemaProducts : karaokeProducts)
        .filter(product => Number(product.price?.rental) > 0)
        .slice(0, 12)
        .map(product => withRentalProductOffer({
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Product',
            name: product.name,
            description: textFromHtml(product.description)
          },
          priceCurrency: 'USD',
          price: Number(product.price.rental),
          availability: 'https://schema.org/InStock',
          url: absoluteProductUrl(product.url)
        })).filter(Boolean)
    }
  }

  const decisionItems = [
    {
      label: 'Audience and room',
      title: 'Start with coverage',
      icon: FiUsers,
      summary: 'Choose enough speaker and display coverage for the farthest guest—not only for the people nearest the system.',
      points: ['Home and small-party packages', 'Larger rooms and celebrations', 'Corporate and group programs']
    },
    {
      label: 'Singers and handoffs',
      title: 'Choose the microphone count',
      icon: FiMic,
      summary: 'Two microphones suit duets and simple rotations. Four microphones make group singing and faster guest changes easier.',
      points: ['Confirm wired or wireless', 'Check the exact included quantity', 'Plan for group participation']
    },
    {
      label: 'Lyrics visibility',
      title: 'Match the display to the room',
      icon: FiMonitor,
      summary: 'Compare TV or projector packages by display size, viewing distance, and where singers and guests will be positioned.',
      points: ['TV packages for compact rooms', 'Projector options for larger viewing', 'Confirm playback and internet needs']
    }
  ]

  const useCases = [
    {
      eyebrow: 'Homes and smaller parties',
      title: 'Compact karaoke machine packages',
      summary: 'Keep the setup straightforward when the singers, display, and audience share one room.',
      body: <p>Start with a ready-to-use package, then confirm the included microphones, lyrics display, speaker coverage, and playback connection before choosing dates.</p>,
      image: karaokeHomeSetup,
      imageAlt: 'Real compact karaoke setup with a television, microphones, mixer, and speaker',
      href: '#orlando-karaoke-catalog',
      actionLabel: 'Compare Karaoke Packages'
    },
    {
      eyebrow: 'Celebrations and group singing',
      title: 'More microphones and wider coverage',
      summary: 'Give guests enough microphones and make the lyrics readable without crowding the performance area.',
      body: <p>Compare packages by microphone count and screen size. For music-driven parties, confirm that the speaker system covers the room and that the package includes the stands, cables, and control equipment shown in its details.</p>,
      image: karaokeMachine,
      imageAlt: 'Real karaoke station with two microphones, a television, mixer, and playback controls',
      href: '/orlando/speaker-rental',
      actionLabel: 'Compare Orlando Speakers'
    },
    {
      eyebrow: 'Corporate and larger-room programs',
      title: 'Karaoke equipment with event support',
      summary: 'Use the managed path when karaoke is one part of a broader program or the venue needs delivery, setup, and coordination.',
      body: <p>This page rents the karaoke equipment. Ask for help when the event also needs an operator, an emcee, several display positions, a schedule around other program segments, or coordinated AV services.</p>,
      image: karaokeSinger,
      imageAlt: 'Singer using a microphone during a live karaoke performance',
      href: '/orlando',
      actionLabel: 'Explore Orlando AV Services'
    }
  ]

  return <>
    <BreadcrumbJsonLd itemListElements={toBreadcrumbJsonLdItems(breadcrumbs)} />
    <NextSeo
      title={TITLE}
      description={DESCRIPTION}
      canonical={CANONICAL}
      twitter={{ handle: '@miamisoundrent1', site: '@miamisoundrent1', cardType: 'summary_large_image' }}
      openGraph={{
        type: 'website',
        url: CANONICAL,
        title: TITLE,
        description: DESCRIPTION,
        images: [{
          url: 'https://miamisoundrental.com/Pictures/categories-v2/orlando-karaoke-rental-hero-v2.jpg',
          width: 1672,
          height: 941,
          alt: 'Complete karaoke machine rental package in an Orlando event room'
        }],
        site_name: 'Miami Sound Rental'
      }}
      additionalMetaTags={[{
        name: 'keywords',
        content: 'karaoke rental Orlando, karaoke machine rental Orlando, rent karaoke machine Orlando, karaoke equipment rental Orlando, karaoke rental Tampa, karaoke rental package'
      }]}
    />
    <LocalBusinessJsonLd
      type='Store'
      id='https://miamisoundrental.com/orlando#localbusiness'
      name='Miami Sound Rental Orlando'
      description='Audio visual equipment rental and event production services from our Orlando branch.'
      url='https://miamisoundrental.com/orlando'
      telephone='+14078070052'
      address={{
        streetAddress: '8600 Commodity Cir #152',
        addressLocality: 'Orlando',
        addressRegion: 'FL',
        postalCode: '32819',
        addressCountry: 'US'
      }}
      geo={{ latitude: '28.445134', longitude: '-81.431191' }}
      images={['https://miamisoundrental.com/Pictures/categories-v2/orlando-karaoke-rental-hero-v2.jpg']}
      openingHours={BUSINESS_HOURS}
      areaServed={[{
        geoMidpoint: { latitude: '28.445134', longitude: '-81.431191' },
        geoRadius: '160000'
      }]}
    />
    <Head>
      <script
        key='orlando-karaoke-rental-service-schema'
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
    </Head>

    <PageShell>
      <ServiceHero
        breadcrumbs={toHeroBreadcrumbs(breadcrumbs)}
        eyebrow='Karaoke machine rental · Orlando, Tampa & Central Florida'
        title='Karaoke Machine Rental in Orlando'
        description='Compare complete karaoke equipment packages with microphones, speakers, playback controls, and a lyrics display. See Orlando inventory first, then choose eligible warehouse pickup or delivery and setup.'
        image={{ src: orlandoKaraokeHero, position: '70% center' }}
        imageAlt='Complete karaoke rental setup with microphones, television, speaker, and mixer in an Orlando event room'
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        processLabel='How Orlando karaoke rental works'
        steps={[
          { icon: 'search', title: 'Compare packages', description: 'Check microphones, sound, and display.' },
          { icon: 'calendar', title: 'Choose dates', description: 'See current Orlando availability.' },
          { icon: 'truck', title: 'Select fulfillment', description: 'Eligible pickup or delivery and setup.' },
          { icon: 'success', title: 'Confirm online', description: 'Review the exact package and price.' }
        ]}
        stackActionsOnMobile
      />

      <ServiceIntroduction
        eyebrow='Complete Orlando karaoke equipment packages'
        heading='Rent the karaoke system—not a venue or karaoke host'
        body={<>
          <p>This page is for people who need the equipment for karaoke at a home, party venue, meeting room, school, or corporate event. Compare ready-made packages instead of assembling unrelated microphones, speakers, displays, and playback equipment one item at a time.</p>
          <p>The catalog opens with equipment available through our Orlando branch. Select <strong>All</strong> only when you also want to compare Miami-fulfilled options. The package details identify the microphones, display, speakers, controls, and other included items before you choose the rental.</p>
        </>}
        guideHeading='Orlando pickup or Central Florida delivery?'
        guideSummary='The available fulfillment path depends on the package, event dates, and support required.'
        guideBody={<>
          <p>Eligible packages can be picked up at our Orlando warehouse. Delivery and setup are available across Orlando and Central Florida, including Tampa when the equipment and travel window are available.</p>
          <p>Select the package and dates first, then enter the event location to see the applicable fulfillment choices and price. Request help before booking if the event needs an operator, emcee, or coordination with other AV systems.</p>
        </>}
        image={karaokeHomeSetup}
        imageAlt='Real karaoke equipment package with television, microphones, mixer, and speaker'
        signals={[
          'Orlando inventory is the default catalog view',
          'Package contents are shown before rental confirmation',
          'Tampa requests are handled by the Orlando team when available'
        ]}
      />

      <VisualDecisionGuide
        id='orlando-karaoke-package-guide'
        eyebrow='Choose the right package'
        heading='Compare coverage, microphones, and lyrics visibility'
        description='A complete package should fit the room and the way guests will participate—not merely have “karaoke” in its name.'
        items={decisionItems}
      />

      <ProductCatalog
        carousel={{
          name: 'Orlando Karaoke Machine Rentals',
          url: CANONICAL,
          description: 'Complete karaoke equipment packages with microphones, speakers, playback controls, and lyrics displays'
        }}
        sortOptions={['Event Size', 'Screen Size', 'Microphones']}
        listName='Orlando Karaoke Package Catalog'
        anchorId='orlando-karaoke-catalog'
        eyebrow='Compare complete karaoke packages'
        description='Showing packages available in Orlando. Select All only when you also want to compare Miami-fulfilled options.'
        products={karaokeProducts}
        inventoryMarket='Orlando'
      />

      <Container>
        <CatalogHelp
          ariaLabel='Karaoke rental assistance'
          heading='Not sure which karaoke package fits your event?'
          description='Our Orlando team can help compare microphone count, speaker coverage, lyrics displays, pickup, and delivery.'
          quoteLabel='Ask a Karaoke Specialist'
          onQuote={openQuote}
          onCall={trackCall}
          phone='407 807 0052'
          phoneHref='tel:+14078070052'
        />
      </Container>

      <MediaFeature
        eyebrow='Know what you are reserving'
        heading='Included equipment varies by karaoke package'
        body={<>
          <p>Complete packages commonly combine microphones, speakers, a mixer or control system, playback equipment, and a TV or projector-based lyrics display. The exact microphone quantity, display size, stands, cables, and software vary by package.</p>
          <p>Use the included-items list as the source of truth. Do not assume that every package has the same song access, internet requirement, microphone type, or screen. If a particular song library or connection matters, confirm it with the Orlando team before booking.</p>
        </>}
        secondaryHeading='Equipment rental and managed support are separate choices'
        secondaryBody={<p>The online catalog is designed for equipment rental. Delivery and setup can be added through the rental flow. An operator, emcee, or managed event schedule is not automatically included; use the help path when the event needs those services.</p>}
        image={karaokeMachine}
        imageAlt='Real karaoke control station with microphones, television, mixer, and playback equipment'
        imagePosition='center'
        reverse
      />

      <EquipmentGuide
        eyebrow='Choose by event format'
        headingId='orlando-karaoke-use-cases'
        title='Choose the Correct Orlando Karaoke Rental Path'
        description='Keep straightforward equipment rentals online and use the managed path when the program needs labor or wider AV coordination.'
        items={useCases}
      />

      <RelatedServices
        eyebrow='Complete the karaoke setup'
        heading='Frequently rented together'
        headingId='orlando-karaoke-related-services'
        description='Use these Orlando pages when the event needs additional speakers, displays, projection, or DJ playback beyond the selected karaoke package.'
        additionalServices={[
          {
            h1: 'Orlando Speaker Rental',
            url: '/orlando/speaker-rental',
            miniText: 'Compare powered speakers and subwoofer packages for wider coverage and music playback.'
          },
          {
            h1: 'Orlando TV Rental',
            url: '/orlando/tv-rental',
            miniText: 'Add a 4K display or monitor when the package display does not fit the room.'
          },
          {
            h1: 'Orlando Projector Rental',
            url: '/orlando/projector-rental',
            miniText: 'Compare projectors and screens for larger lyrics viewing and presentation use.'
          },
          {
            h1: 'Orlando DJ Equipment Rental',
            url: '/orlando/dj-equipment-rental',
            miniText: 'Add professional playback and DJ equipment for the rest of the event program.'
          },
          {
            h1: 'Orlando AV Rental',
            url: '/orlando',
            miniText: 'Explore the equipment and managed services available from the Orlando branch.'
          }
        ]}
      />

      <FAQGroup
        questions={questions}
        title='Orlando Karaoke Machine Rental Questions'
        headingId='orlando-karaoke-rental-faq-title'
        intro='Answers about package contents, microphones, lyrics displays, song access, Orlando pickup, Tampa delivery, operators, and sizing.'
      />

      <ServiceCategoryGrid
        orlando
        currentCategory='sound'
        description='Explore equipment categories with dedicated Orlando pages, or return to the Orlando hub for the complete local service map.'
      />

      <ReviewProof
        filter={['party', 'private', 'karaoke', 'orlando']}
        heading='Recent feedback relevant to parties and event service'
        description='Showing verified Google feedback selected for party, private-event, service, and Orlando relevance when available; reviews are not presented as karaoke-specific unless the reviewer says so.'
        initialData={reviewData}
        pagePath={PAGE_PATH}
        reviewsUrl={ORLANDO_REVIEWS_URL}
        sectionId='orlando-karaoke-review-proof'
      />

      <AudioClosingActionBand
        pagePath={PAGE_PATH}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      />
    </PageShell>

    {user?.email === 'pedro@drynwet.com' && user?.employee && (
      <SeoPageDrawer pagePath={PAGE_PATH} />
    )}
  </>
}

export default OrlandoKaraokeRental
